import { isKeeperTheme } from 'fym-shared/src/music24/assetKinds';
import { config } from '../../../../config/config';
import { SMLRootParamList, SMLRouteNames } from '../../../../routing/sml/SMLRoutes';

/** The card that a link can scroll to and highlight. Only the AI reel card (on the Posts tab) today. */
export type ReleaseFocus = 'aiReel';

/** The query value `focus=aiReel`: the song page scrolls to the AI reel card and highlights it. */
export const RELEASE_FOCUS_AI_REEL: ReleaseFocus = 'aiReel';

/** Reads the `focus` query value. An unknown value gives undefined, so the page opens at the top. */
export function parseReleaseFocus(value: string | undefined | null): ReleaseFocus | undefined {
  return value === RELEASE_FOCUS_AI_REEL ? RELEASE_FOCUS_AI_REEL : undefined;
}

/**
 * The route params of the release page on the Kit tab (design assets-rethink b1). `focus=aiReel`
 * opens the Posts tab (an AI reel is a post file) and scrolls to that card.
 */
export function releasePageParams(
  catalogItemUuid: string,
  focus?: ReleaseFocus
): SMLRootParamList[SMLRouteNames.promoteRelease] {
  return focus === undefined ? { catalogItemUuid, tab: 'kit' } : { catalogItemUuid, tab: 'posts', focus };
}

/**
 * The route params of one asset in its release (design assets-rethink b1, c1): the tab of its kind
 * (Kit for a keeper, Posts for a post) with the asset viewer open on `assetUuid` (a kit asset or a
 * reel), at revision `revision` when given. With no `themeId` the old `tab=assets` goes out and the
 * page picks the tab by the kind of the asset (`resolveReleaseTab`).
 */
export function releaseAssetParams(
  catalogItemUuid: string,
  assetUuid: string,
  revision?: number | null,
  themeId?: string
): SMLRootParamList[SMLRouteNames.promoteRelease] {
  const tab = themeId === undefined ? 'assets' : isKeeperTheme(themeId) ? 'kit' : 'posts';
  return revision === undefined || revision === null
    ? { catalogItemUuid, tab, asset: assetUuid }
    : { catalogItemUuid, tab, asset: assetUuid, rev: String(revision) };
}

/** The query value `theme=edit`: the Overview tab opens with the theme editor open. */
export const RELEASE_THEME_EDIT = 'edit';

/**
 * The path of the release page with the theme editor open (QA 2026-10-28, mob-6: "Pick a look" of
 * the first-release checklist opened the Overview at the top, and the Theme panel was four phone
 * screens down). The route type has no `theme` key, so the checklist navigates to this path.
 */
export function releaseThemeEditPath(catalogItemUuid: string, accountId?: number): string {
  const query = new URLSearchParams({ catalogItemUuid, tab: 'overview', theme: RELEASE_THEME_EDIT });
  if (accountId !== undefined) {
    query.set('accountId', String(accountId));
  }
  return `/${SMLRouteNames.promote}/${SMLRouteNames.promoteRelease}?${query.toString()}`;
}

/** Reads `?rev=`: a whole number of 1 or more, else undefined. */
export function parseReleaseRevision(value: string | undefined | null): number | undefined {
  if (value === undefined || value === null || !/^[1-9]\d{0,5}$/.test(value)) {
    return undefined;
  }
  return Number(value);
}

/**
 * The native song page of a catalog row:
 * `<scheme>://promote/promoteRelease?catalogItemUuid=<uuid>&tab=assets[&accountId=<id>]`.
 * The iOS and Android apps read this link and open their own song page on the Assets tab. The
 * `accountId` makes the app select that artist first.
 */
export function releasePageDeepLink(
  catalogItemUuid: string,
  accountId: number | null,
  scheme: string = config.nativeAppScheme
): string {
  const query = new URLSearchParams({ catalogItemUuid, tab: 'assets' });
  if (accountId !== null) {
    query.set('accountId', String(accountId));
  }
  return `${scheme}://promote/promoteRelease?${query.toString()}`;
}
